import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash } from 'react-icons/fa';
import api from '../api';
import { norm, formatFatiasKg } from '../utils';
import { useConfirm } from './ConfirmProvider';
import useViewMode from '../hooks/useViewMode';
import ExemploBadge from './ExemploBadge';
import ViewToggle from './ViewToggle';
import { Link } from 'react-router-dom';

const FORM_INICIAL = {
  nome: '', fatias: '', tipo_medida: 'fatias', tipo: 'imediato',
  forma_tamanho: '', cakeboard_tamanho: '', massa_crua_por_forma: '',
  discos_por_forma: '2',
  discos_camadas: '', discos_gramas: '',
  calda_camadas: '', calda_gramas: '',
  cinta_camadas: '', cinta_gramas: '',
  cobertura_camadas: '', cobertura_gramas: '',
  recheio_camadas: '3',
  preco: '',
};

const noScrollNumber = {
  onWheel: (e) => e.currentTarget.blur(),
  onKeyDown: (e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault(); },
};

function gerarPares(lista) {
  const pares = [];
  for (let i = 0; i < lista.length; i++)
    for (let j = i + 1; j < lista.length; j++)
      pares.push({ a: lista[i], b: lista[j] });
  return pares;
}

function Progresso({ step, total, onJump, podeIr, isCongelado }) {
  const labels = ['Dados básicos', 'Massas', 'Recheios', isCongelado ? 'Menu Fixo' : 'Combinações'];
  return (
    <>
    {/* Celular: os circulos numerados ocupavam uma faixa inteira para dizer
        "1 2 3" — os nomes dos passos ficam escondidos nesse tamanho. Uma linha
        de texto com barra fina diz mais em um terco da altura. */}
    <div className="sm:hidden mb-3">
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <span className="text-sm font-semibold text-gray-700 truncate">{labels[step - 1]}</span>
        <span className="text-[11px] font-semibold text-gray-400 shrink-0">{step} de {total}</span>
      </div>
      <div className="h-1 rounded bg-gray-100 overflow-hidden">
        <div className="h-full bg-pink-500 rounded transition-all" style={{ width: `${(step / total) * 100}%` }} />
      </div>
    </div>
    <div className="hidden sm:flex items-center gap-1 mb-6">
      {Array.from({ length: total }, (_, i) => i + 1).map((s, i) => {
        const clicavel = !!onJump && podeIr && podeIr(s);
        return (
        <React.Fragment key={s}>
          <div
            className={`flex items-center gap-1.5 min-w-0 ${clicavel ? 'cursor-pointer' : ''}`}
            onClick={() => clicavel && onJump(s)}
            title={clicavel ? `Ir para ${labels[s - 1]}` : undefined}
          >
            <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold transition-colors ${
              step > s ? 'bg-green-500 text-white' :
              step === s ? 'bg-pink-600 text-white' :
              clicavel ? 'bg-gray-200 text-gray-500 hover:bg-pink-100 hover:text-pink-600' :
              'bg-gray-100 text-gray-400'
            }`}>
              {step > s ? '✓' : s}
            </div>
            <span className={`text-xs font-medium hidden sm:block truncate ${step === s ? 'text-pink-600' : clicavel ? 'text-gray-500' : 'text-gray-400'}`}>
              {labels[s - 1]}
            </span>
          </div>
          {i < total - 1 && (
            <div className={`flex-1 h-0.5 min-w-4 rounded ${step > s ? 'bg-green-400' : 'bg-gray-100'}`} />
          )}
        </React.Fragment>
        );
      })}
    </div>
    </>
  );
}

export default function GuiaTamanhos() {
  const [tamanhos, setTamanhos] = useState([]);
  const [fichasMassa, setFichasMassa] = useState([]);
  const [fichasRecheio, setFichasRecheio] = useState([]);
  const [todasFichas, setTodasFichas] = useState([]);
  const [recheiosExistentes, setRecheiosExistentes] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [embalagensList, setEmbalagensList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const confirm = useConfirm();

  const [wizardAberto, setWizardAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [massasInativasVinculadas, setMassasInativasVinculadas] = useState([]);
  const [step, setStep] = useState(1);
  const [salvando, setSalvando] = useState(false);
  const [salvarMsg, setSalvarMsg] = useState('');

  // Filtros da lista
  const [busca, setBusca] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('todos');
  const [menuFixoLista, setMenuFixoLista] = useState([]);

  function faixaPrecoCongelado(tamanhoId) {
    const skus = menuFixoLista.filter(m => m.tamanho_id === tamanhoId && m.preco != null && m.preco > 0);
    if (skus.length === 0) return null;
    const precos = skus.map(m => Number(m.preco));
    const min = Math.min(...precos);
    const max = Math.max(...precos);
    const fmt = (v) => `R$ ${v.toFixed(2).replace('.', ',')}`;
    return min === max ? fmt(min) : `${fmt(min)} – ${fmt(max)}`;
  }

  // Step 1
  const [form, setForm] = useState(FORM_INICIAL);
  // Step 1 - embalagens
  const [embalagensSelecionadas, setEmbalagensSelecionadas] = useState([]);
  // Step 2
  const [massesSelecionadas, setMassesSelecionadas] = useState([]);
  // Step 3 — lista de { id, tempId, nome, isNew, base_tipo, base_ficha_id, base_produto_id,
  //                      tem_complemento, complemento_tipo, complemento_ficha_id, complemento_produto_id,
  //                      gramas_base, gramas_complemento }
  const [recheiosSelecionados, setRecheiosSelecionados] = useState([]);
  // Step 4 — lista de { key, nomeA, nomeB, keyA, keyB, camadas_a, camadas_b, nome }
  const [combinacoes, setCombinacoes] = useState([]);
  const [combsRegistradas, setCombsRegistradas] = useState([]);
  // Step 4 (congelado) — SKUs do menu fixo
  const [skusMenu, setSkusMenu] = useState([]);
  const [skusOriginais, setSkusOriginais] = useState([]);

  useEffect(() => { carregarDados(); }, []);

  async function carregarDados() {
    setLoading(true);
    try {
      const [resTam, resFichas, resRecheios, resProd, resCombsReg, resEmb, resMenu] = await Promise.all([
        api.get('/guia/tamanhos'),
        api.get('/fichas/api/fichas-tecnicas'),
        api.get('/guia/recheios'),
        api.get('/produtos'),
        api.get('/guia/combinacoes'),
        api.get('/api/embalagens'),
        api.get('/guia/menu-fixo'),
      ]);
      setTamanhos(resTam.data);
      setMenuFixoLista(resMenu.data || []);
      const lista = resFichas.data?.fichas || [];
      setFichasMassa(lista.filter(f => f.categoria?.toLowerCase() === 'massas de bolo'));
      setFichasRecheio(lista.filter(f => f.categoria?.toLowerCase() === 'recheios'));
      setTodasFichas(lista);
      setRecheiosExistentes(resRecheios.data || []);
      setProdutos(resProd.data || []);
      setEmbalagensList(resEmb.data || []);
      setCombsRegistradas(resCombsReg.data || []);
    } catch {
      setErro('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }

  // ── Abrir wizard ─────────────────────────────────────────────────────────────
  function abrirNovo() {
    setForm(FORM_INICIAL);
    setMassesSelecionadas([]);
    setMassasInativasVinculadas([]);
    setRecheiosSelecionados([]);
    setCombinacoes([]);
    setSkusMenu([]);
    setSkusOriginais([]);
    setEmbalagensSelecionadas([]);
    setEditando(null);
    setStep(1);
    setErro('');
    setWizardAberto(true);
  }

  function abrirEditar(t) {
    setForm({
      nome: t.nome, fatias: t.fatias, tipo_medida: t.tipo_medida || 'fatias', tipo: t.tipo,
      forma_tamanho: t.forma_tamanho || '',
      cakeboard_tamanho: t.cakeboard_tamanho || '',
      massa_crua_por_forma: t.massa_crua_por_forma,
      discos_por_forma: t.discos_por_forma ?? 2,
      discos_camadas: t.discos_camadas, discos_gramas: t.discos_gramas,
      calda_camadas: t.calda_camadas, calda_gramas: t.calda_gramas,
      cinta_camadas: t.cinta_camadas, cinta_gramas: t.cinta_gramas,
      cobertura_camadas: t.cobertura_camadas, cobertura_gramas: t.cobertura_gramas,
      recheio_camadas: t.recheio_camadas,
      preco: t.preco ? parseFloat(t.preco).toFixed(2).replace('.', ',') : '',
    });
    const idsVinculados = (t.massas_permitidas || []).map(m => Number(m.ficha_tecnica_id));
    setMassesSelecionadas(idsVinculados);
    // Inclui qualquer massa vinculada que não esteja na lista padrão (categoria diferente, inativa, etc.)
    const naoNaLista = (t.massas_permitidas || [])
      .filter(m => !fichasMassa.some(f => Number(f.id) === Number(m.ficha_tecnica_id)))
      .map(m => ({ id: Number(m.ficha_tecnica_id), nome: m.ficha_tecnica_nome, ativo: m.ficha_tecnica_ativo !== false }));
    setMassasInativasVinculadas(naoNaLista);
    setEmbalagensSelecionadas((t.embalagens || []).map(e => ({ embalagem_id: e.embalagem_id, quantidade: e.quantidade })));

    const recheiosDoTamanho = (t.recheios_permitidos || []).map(r => {
      const temCompl = !!(r.complemento_ficha_id || r.complemento_produto_id);
      return {
        id: r.recheio_id,
        tempId: null,
        nome: r.nome,
        isNew: false,
        tem_complemento: temCompl,
        gramas_base: r.gramas_base ?? '',
        gramas_complemento: r.gramas_complemento ?? '',
      };
    });
    setRecheiosSelecionados(recheiosDoTamanho);
    setCombinacoes([]);
    setSkusMenu([]);
    setSkusOriginais([]);
    setEditando(t.id);
    setStep(1);
    setErro('');
    setWizardAberto(true);

    if (t.tipo === 'congelado') {
      api.get(`/guia/menu-fixo?tamanho_id=${t.id}`).then(res => {
        const carregados = (res.data || []).map(m => ({
          key: `${m.massa_ficha_id}_${m.recheio_id}`,
          massa_ficha_id: Number(m.massa_ficha_id),
          massa_nome: m.massa_ficha_nome,
          recheio_id: Number(m.recheio_id),
          recheio_nome: m.recheio_nome,
          preco: m.preco != null ? parseFloat(m.preco).toFixed(2).replace('.', ',') : '',
          menu_id: m.id,
          ativo: true,
        }));
        setSkusMenu(carregados);
        setSkusOriginais(carregados.map(s => ({ ...s })));
      }).catch(() => {});
    }
  }

  function fecharWizard() { setWizardAberto(false); setErro(''); }

  // ── Step 1 ───────────────────────────────────────────────────────────────────
  function handleChange(e) {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
  }

  function step1Valido() {
    return form.nome && form.fatias && form.massa_crua_por_forma &&
      form.discos_camadas && form.discos_gramas;
  }

  function step2Valido() {
    return massesSelecionadas.length > 0;
  }

  function step3Valido() {
    return recheiosSelecionados.length > 0;
  }

  // ── Step 3 — recheios ────────────────────────────────────────────────────────
  function toggleRecheioExistente(r) {
    const jaEsta = recheiosSelecionados.find(x => x.id === r.id);
    if (jaEsta) {
      setRecheiosSelecionados(p => p.filter(x => x.id !== r.id));
    } else {
      const temCompl = !!(r.complemento_ficha_id || r.complemento_produto_id);
      setRecheiosSelecionados(p => [...p, {
        id: r.id, tempId: null, nome: r.nome, isNew: false,
        tem_complemento: temCompl,
        gramas_base: '', gramas_complemento: '',
      }]);
    }
  }

  function adicionarNovoRecheio() {
    setRecheiosSelecionados(p => [...p, {
      id: null, tempId: Date.now(), nome: '',
      isNew: true,
      base_tipo: 'ficha', base_ficha_id: '', base_produto_id: '',
      tem_complemento: false,
      complemento_tipo: 'ficha', complemento_ficha_id: '', complemento_produto_id: '',
      gramas_base: '', gramas_complemento: '',
    }]);
  }

  function chaveRecheio(r) { return r.id ?? r.tempId; }

  function atualizarRecheio(key, campo, valor) {
    setRecheiosSelecionados(p => p.map(r => {
      if (chaveRecheio(r) !== key) return r;
      const u = { ...r, [campo]: valor };
      if (campo === 'base_ficha_id' && valor && !u.nome) {
        const f = todasFichas.find(f => f.id === Number(valor));
        if (f) u.nome = f.nome;
      }
      return u;
    }));
  }

  function removerRecheio(key) {
    setRecheiosSelecionados(p => p.filter(r => chaveRecheio(r) !== key));
  }

  // ── Step 4 — combinações ─────────────────────────────────────────────────────
  function inicializarCombinacoes() {
    setCombinacoes(paresSemCombinacao.map(({ a, b }) => ({
      key: `${chaveRecheio(a)}_${chaveRecheio(b)}`,
      nomeA: a.nome, nomeB: b.nome,
      keyA: chaveRecheio(a), keyB: chaveRecheio(b),
      camadas_a: 2, camadas_b: 1,
      nome: `${a.nome} + ${b.nome}`,
    })));
  }

  function atualizarCombinacao(key, campo, valor) {
    setCombinacoes(p => p.map(c => {
      if (c.key !== key) return c;
      const u = { ...c, [campo]: Number(valor) };
      if (campo === 'camadas_a') u.camadas_b = Number(form.recheio_camadas) - Number(valor);
      return u;
    }));
  }

  // ── Navegação ────────────────────────────────────────────────────────────────
  const nCamadas = Number(form.recheio_camadas);
  const pares = gerarPares(recheiosSelecionados);
  const paresSemCombinacao = pares.filter(({ a, b }) => {
    const idA = a.id ?? null;
    const idB = b.id ?? null;
    if (!idA || !idB) return true; // recheios novos sempre precisam
    return !combsRegistradas.some(c =>
      (c.recheio_principal_id === idA && c.recheio_secundario_id === idB) ||
      (c.recheio_principal_id === idB && c.recheio_secundario_id === idA)
    );
  });
  const isCongelado = form.tipo === 'congelado';
  const precisaCombinacoes = !editando && nCamadas === 3 && paresSemCombinacao.length > 0;
  const precisaMenu = isCongelado;
  const totalSteps = (precisaCombinacoes || precisaMenu) ? 4 : 3;
  const isLastStep = step === totalSteps;

  function nomeMassaPorId(id) {
    const f = fichasMassa.find(x => Number(x.id) === Number(id));
    if (f) return f.nome;
    const inativa = massasInativasVinculadas.find(x => Number(x.id) === Number(id));
    return inativa?.nome || '';
  }

  function inicializarSkusMenu() {
    setSkusMenu(prev => {
      const existentes = new Map(prev.map(s => [s.key, s]));
      const result = [];
      for (const massaId of massesSelecionadas) {
        for (const r of recheiosSelecionados) {
          const recheioKey = chaveRecheio(r);
          const recheioId = r.id ?? null;
          const key = `${massaId}_${recheioId ?? `tmp${recheioKey}`}`;
          if (existentes.has(key)) {
            result.push(existentes.get(key));
          } else {
            result.push({
              key,
              massa_ficha_id: Number(massaId),
              massa_nome: nomeMassaPorId(massaId),
              recheio_id: recheioId,
              recheio_tempKey: recheioId ? null : recheioKey,
              recheio_nome: r.nome,
              preco: '',
              menu_id: null,
              ativo: false,
            });
          }
        }
      }
      // Mantém também SKUs já cadastrados que não casam (ex: massa removida do passo 2)
      for (const s of prev) {
        if (s.menu_id && !result.some(r => r.key === s.key)) result.push(s);
      }
      return result;
    });
  }

  function avancar() {
    if (step === 3 && precisaCombinacoes) inicializarCombinacoes();
    if (step === 3 && precisaMenu) inicializarSkusMenu();
    setStep(s => s + 1);
  }

  function toggleSku(key) {
    setSkusMenu(p => p.map(s => s.key === key ? { ...s, ativo: !s.ativo } : s));
  }
  function atualizarPrecoSku(key, valor) {
    const raw = String(valor).replace(/\D/g, '');
    const formatado = raw ? (parseInt(raw, 10) / 100).toFixed(2).replace('.', ',') : '';
    setSkusMenu(p => p.map(s => s.key === key ? { ...s, preco: formatado } : s));
  }

  // ── Salvar ───────────────────────────────────────────────────────────────────
  async function salvar() {
    setSalvando(true);
    setSalvarMsg('Salvando tamanho...');
    setErro('');
    try {
      // 1. Tamanho
      // 1. Cria recheios novos PRIMEIRO (precisamos do id real para enviar com o tamanho)
      const recheiosNovos = recheiosSelecionados.filter(r => r.isNew);
      if (recheiosNovos.length) setSalvarMsg(`Criando ${recheiosNovos.length} recheio${recheiosNovos.length > 1 ? 's' : ''} novo${recheiosNovos.length > 1 ? 's' : ''}...`);

      const recheiosResolvidos = [];
      for (const [i, r] of recheiosSelecionados.entries()) {
        let recheioId = r.id;
        if (r.isNew) {
          setSalvarMsg(`Criando recheio "${r.nome}"... (${i + 1}/${recheiosSelecionados.length})`);
          const payload = {
            nome: r.nome,
            base_ficha_id: r.base_tipo === 'ficha' ? Number(r.base_ficha_id) || null : null,
            base_produto_id: r.base_tipo === 'produto' ? Number(r.base_produto_id) || null : null,
            complemento_ficha_id: r.tem_complemento && r.complemento_tipo === 'ficha' ? Number(r.complemento_ficha_id) || null : null,
            complemento_produto_id: r.tem_complemento && r.complemento_tipo === 'produto' ? Number(r.complemento_produto_id) || null : null,
          };
          const res = await api.post('/guia/recheios', payload);
          recheioId = res.data.id;
        }
        recheiosResolvidos.push({ ...r, resolvedId: recheioId });
      }

      // 2. Tamanho + recheios permitidos + quantidades (tudo num PUT só)
      const payloadTam = {
        ...form,
        fatias: Number(String(form.fatias).replace(',', '.')),
        tipo_medida: form.tipo_medida || 'fatias',
        massa_crua_por_forma: Number(form.massa_crua_por_forma),
        discos_por_forma: Number(form.discos_por_forma) || 2,
        discos_camadas: Number(form.discos_camadas), discos_gramas: Number(form.discos_gramas),
        calda_camadas: Number(form.calda_camadas), calda_gramas: Number(form.calda_gramas),
        cinta_camadas: Number(form.cinta_camadas), cinta_gramas: Number(form.cinta_gramas),
        cobertura_camadas: Number(form.cobertura_camadas), cobertura_gramas: Number(form.cobertura_gramas),
        recheio_camadas: Number(form.recheio_camadas),
        preco: parseFloat(String(form.preco).replace(',', '.')) || 0,
        massas: massesSelecionadas,
        embalagens: embalagensSelecionadas.filter(e => e.embalagem_id),
        recheios: recheiosResolvidos.map(r => ({
          recheio_id: r.resolvedId,
          gramas_base: Number(r.gramas_base) || 0,
          gramas_complemento: r.gramas_complemento ? Number(r.gramas_complemento) : 0,
        })),
      };
      setSalvarMsg('Salvando tamanho e recheios...');
      let tamanhoId;
      if (editando) {
        await api.put(`/guia/tamanhos/${editando}`, payloadTam);
        tamanhoId = editando;
      } else {
        const res = await api.post('/guia/tamanhos', payloadTam);
        tamanhoId = res.data.id;
      }

      // 3. Combinações
      if (combinacoes.length) {
        setSalvarMsg(`Salvando ${combinacoes.length} combinaç${combinacoes.length > 1 ? 'ões' : 'ão'}...`);
        for (const c of combinacoes) {
          const rA = recheiosResolvidos.find(r => chaveRecheio(r) === c.keyA);
          const rB = recheiosResolvidos.find(r => chaveRecheio(r) === c.keyB);
          if (!rA || !rB) continue;
          await api.post('/guia/combinacoes', {
            nome: c.nome,
            recheio_principal_id: rA.resolvedId,
            recheio_secundario_id: rB.resolvedId,
            camadas_principal: c.camadas_a,
            camadas_secundario: c.camadas_b,
          });
        }
      }

      // 4. Sincronizar Menu Fixo (apenas para tamanhos congelados)
      if (isCongelado) {
        setSalvarMsg('Sincronizando menu de congelados...');
        const resolveRecheioId = (s) => {
          if (s.recheio_id) return s.recheio_id;
          if (s.recheio_tempKey) {
            const r = recheiosResolvidos.find(x => chaveRecheio(x) === s.recheio_tempKey);
            return r?.resolvedId || null;
          }
          return null;
        };
        const ativosAgora = skusMenu.filter(s => s.ativo);
        const parsePreco = p => parseFloat(String(p).replace(',', '.')) || 0;

        for (const s of ativosAgora.filter(x => !x.menu_id)) {
          const recheioIdReal = resolveRecheioId(s);
          if (!recheioIdReal) continue;
          await api.post('/guia/menu-fixo', {
            tamanho_id: tamanhoId,
            massa_ficha_id: Number(s.massa_ficha_id),
            recheio_id: Number(recheioIdReal),
            preco: parsePreco(s.preco),
          });
        }
        for (const s of ativosAgora.filter(x => x.menu_id)) {
          const orig = skusOriginais.find(o => o.menu_id === s.menu_id);
          if (orig && parsePreco(orig.preco) !== parsePreco(s.preco)) {
            await api.put(`/guia/menu-fixo/${s.menu_id}`, { preco: parsePreco(s.preco) });
          }
        }
        const idsAtivos = new Set(ativosAgora.filter(x => x.menu_id).map(x => x.menu_id));
        for (const orig of skusOriginais) {
          if (orig.menu_id && !idsAtivos.has(orig.menu_id)) {
            await api.delete(`/guia/menu-fixo/${orig.menu_id}`);
          }
        }
      }

      setSalvarMsg('✓ Tudo salvo!');
      await new Promise(r => setTimeout(r, 600));
      setSucesso(editando ? 'Tamanho atualizado!' : 'Tamanho cadastrado com sucesso!');
      fecharWizard();
      carregarDados();
    } catch (e) {
      setErro(e.response?.data?.erro || 'Erro ao salvar');
    } finally {
      setSalvando(false);
      setSalvarMsg('');
    }
  }

  async function desativar(id) {
    if (!await confirm('Desativar este tamanho?')) return;
    try {
      await api.delete(`/guia/tamanhos/${id}`);
      setSucesso('Tamanho desativado.');
      carregarDados();
    } catch {
      setErro('Erro ao desativar tamanho');
    }
  }

  // ── Filtros ──────────────────────────────────────────────────────────────────
  const tamanhosFiltrados = tamanhos.filter(t => {
    const matchBusca = !busca || norm(t.nome).includes(norm(busca));
    const matchTipo = tipoFiltro === 'todos' || t.tipo === tipoFiltro;
    return matchBusca && matchTipo;
  });
  const { mode: vmMode, setMode: setVmMode, effective: vmEffective } = useViewMode('view:tamanhos');
  const tamanhosImediatos = tamanhosFiltrados.filter(t => t.tipo === 'imediato');
  const tamanhosCongelados = tamanhosFiltrados.filter(t => t.tipo === 'congelado');

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-6xl mx-auto">

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-700">Tamanhos de Bolo</h1>
          <p className="text-gray-400 text-sm mt-1">Cadastro dos tamanhos, formas e padrões de montagem</p>
        </div>
        <button onClick={abrirNovo}
          className="bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 text-sm">
          + Novo Tamanho
        </button>
      </div>

      {erro && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">{erro}</div>}
      {sucesso && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 text-sm">{sucesso}</div>}

      {/* Filtros */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="relative">
          <span className="absolute left-3 top-2.5 text-gray-400 text-sm">🔍</span>
          <input
            type="text"
            placeholder="Buscar por nome..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
            className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-300 focus:border-transparent"
          />
          {busca && (
            <button onClick={() => setBusca('')}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 text-sm">✕</button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <select
            value={tipoFiltro}
            onChange={e => setTipoFiltro(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-300">
            <option value="todos">🎂 Todos os tipos</option>
            <option value="imediato">🍰 Frescos</option>
            <option value="congelado">❄️ Congelados</option>
          </select>
          <ViewToggle mode={vmMode} setMode={setVmMode} />
        </div>
      </div>

      {/* Filtros ativos */}
      {(busca || tipoFiltro !== 'todos') && (
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-500">Filtros ativos:</span>
          {busca && (
            <span className="bg-pink-100 text-pink-700 px-3 py-1 rounded-full text-sm flex items-center gap-2">
              Busca: "{busca}"
              <span className="cursor-pointer hover:text-pink-900" onClick={() => setBusca('')}>✕</span>
            </span>
          )}
          {tipoFiltro !== 'todos' && (
            <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm flex items-center gap-2">
              {tipoFiltro === 'imediato' ? '🍰 Frescos' : '❄️ Congelados'}
              <span className="cursor-pointer hover:text-blue-900" onClick={() => setTipoFiltro('todos')}>✕</span>
            </span>
          )}
          <button onClick={() => { setBusca(''); setTipoFiltro('todos'); }}
            className="text-sm text-red-500 hover:text-red-700 underline">Limpar todos</button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-400">Carregando...</div>
      ) : tamanhosFiltrados.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">Nenhum tamanho encontrado</p>
          <p className="text-gray-400 text-sm mt-2">
            {busca || tipoFiltro !== 'todos' ? 'Tente ajustar os filtros' : 'Clique em "+ Novo Tamanho" para começar'}
          </p>
        </div>
      ) : (
        <>
          {/* ── Imediatos ── */}
          {(tipoFiltro === 'todos' || tipoFiltro === 'imediato') && tamanhosImediatos.length > 0 && (
            <div className="mb-8">
              <h3 className="text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm">
                  🍰 Frescos ({tamanhosImediatos.length})
                </span>
              </h3>
              {vmEffective === 'cards' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {tamanhosImediatos.map(t => (
                    <TamanhoCard
                      key={t.id} t={t} accent="orange"
                      preco={t.preco ? `R$ ${t.preco.toFixed(2).replace('.', ',')}` : '—'}
                      onEdit={() => abrirEditar(t)} onDelete={() => desativar(t.id)}
                    />
                  ))}
                </div>
              ) : (
                <TamanhosTable
                  rows={tamanhosImediatos}
                  precoFor={(t) => t.preco ? `R$ ${t.preco.toFixed(2).replace('.', ',')}` : '—'}
                  recheioFor={(t) => `${t.recheio_camadas} cam.`}
                  onEdit={abrirEditar} onDelete={desativar}
                />
              )}
            </div>
          )}

          {/* ── Congelados ── */}
          {(tipoFiltro === 'todos' || tipoFiltro === 'congelado') && tamanhosCongelados.length > 0 && (
            <div className="mb-8">
              <h3 className="text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">
                  ❄️ Congelados ({tamanhosCongelados.length})
                </span>
              </h3>
              {vmEffective === 'cards' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {tamanhosCongelados.map(t => {
                    const faixa = faixaPrecoCongelado(t.id);
                    const nSkus = menuFixoLista.filter(m => m.tamanho_id === t.id).length;
                    return (
                      <TamanhoCard
                        key={t.id} t={t} accent="blue"
                        preco={faixa || '—'}
                        precoTitle={nSkus > 0 ? `${nSkus} sabor${nSkus > 1 ? 'es' : ''} fixo${nSkus > 1 ? 's' : ''}` : 'Sem menu fixo cadastrado'}
                        precoSubtitle={nSkus > 0 ? `${nSkus} sabor${nSkus > 1 ? 'es' : ''}` : null}
                        onEdit={() => abrirEditar(t)} onDelete={() => desativar(t.id)}
                      />
                    );
                  })}
                </div>
              ) : (
                <TamanhosTable
                  rows={tamanhosCongelados}
                  precoFor={(t) => faixaPrecoCongelado(t.id) || '—'}
                  recheioFor={(t) => `${t.recheio_camadas} cam.`}
                  onEdit={abrirEditar} onDelete={desativar}
                />
              )}
            </div>
          )}

          {/* Rodapé */}
          <div className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex flex-col md:flex-row justify-between items-center gap-3">
              <span className="font-medium text-gray-700 text-sm">
                Total: {tamanhosFiltrados.length} {tamanhosFiltrados.length === 1 ? 'tamanho' : 'tamanhos'}
              </span>
              <div className="flex gap-3">
                <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-medium">
                  🍰 {tamanhos.filter(t => t.tipo === 'imediato').length} Frescos
                </span>
                <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                  ❄️ {tamanhos.filter(t => t.tipo === 'congelado').length} Congelados
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══════════════════════════ WIZARD ═══════════════════════════ */}
      {wizardAberto && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(61,44,44,0.3)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, overflow: 'auto', padding: '32px 12px' }}>
          <div style={{ background: '#fff', borderRadius: 20, boxShadow: '0 20px 60px rgba(61,44,44,0.16)', width: '100%', maxWidth: 640, margin: '0 auto' }}>

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2.5 sm:px-6 sm:py-4 border-b border-gray-100">
              <h2 className="text-base sm:text-lg font-bold text-gray-800">
                {editando ? 'Editar Tamanho' : 'Novo Tamanho'}
              </h2>
              <button onClick={fecharWizard} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none">×</button>
            </div>

            <div className="px-4 pt-3 pb-0 sm:px-6 sm:pt-5 sm:pb-1">
              <Progresso
                step={step}
                total={totalSteps}
                isCongelado={isCongelado}
                onJump={(s) => {
                  if (s === 4 && precisaCombinacoes) inicializarCombinacoes();
                  if (s === 4 && precisaMenu) inicializarSkusMenu();
                  setStep(s);
                }}
                podeIr={(s) => {
                  // Em edição: pode pular livremente entre os passos.
                  // Em criação: só permite voltar para passos anteriores.
                  if (editando) return s !== step;
                  return s < step;
                }}
              />
            </div>

            <div className="px-4 pb-4 sm:px-6 max-h-[68vh] overflow-y-auto">

              {/* ── Step 1: Dados básicos ── */}
              {step === 1 && (
                <div className="space-y-4">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Identificação</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                      <input name="nome" type="text" value={form.nome} onChange={handleChange}
                        placeholder="ex: 15cm"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-sm font-medium text-gray-700">{form.tipo_medida === 'kg' ? 'Quilos *' : 'Fatias *'}</label>
                        <div className="inline-flex border border-gray-200 rounded-lg overflow-hidden text-xs">
                          <button type="button"
                            onClick={() => setForm(f => ({ ...f, tipo_medida: 'fatias' }))}
                            className={`px-2 py-0.5 ${form.tipo_medida !== 'kg' ? 'bg-pink-500 text-white' : 'bg-white text-gray-500'}`}>
                            fatias
                          </button>
                          <button type="button"
                            onClick={() => setForm(f => ({ ...f, tipo_medida: 'kg' }))}
                            className={`px-2 py-0.5 ${form.tipo_medida === 'kg' ? 'bg-pink-500 text-white' : 'bg-white text-gray-500'}`}>
                            kg
                          </button>
                        </div>
                      </div>
                      <input name="fatias" type="number" value={form.fatias} onChange={handleChange}
                        placeholder={form.tipo_medida === 'kg' ? '1,5' : '18'}
                        step={form.tipo_medida === 'kg' ? '0.1' : '1'}
                        {...noScrollNumber}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tamanho da forma</label>
                      <input name="forma_tamanho" type="text" value={form.forma_tamanho} onChange={handleChange}
                        placeholder="ex: 15cm"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cakeboard</label>
                      <select name="cakeboard_tamanho" value={form.cakeboard_tamanho} onChange={handleChange}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300">
                        <option value="">Selecione...</option>
                        {embalagensList.filter(e => (e.categoria || '').toLowerCase() === 'cakeboards').map(e => (
                          <option key={e.id} value={e.nome}>{e.nome}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
                      <select name="tipo" value={form.tipo} onChange={handleChange}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300">
                        <option value="imediato">🍰 Fresco</option>
                        <option value="congelado">❄️ Congelado</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Camadas de recheio *</label>
                      <select name="recheio_camadas" value={form.recheio_camadas} onChange={handleChange}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300">
                        <option value="1">1 camada</option>
                        <option value="2">2 camadas</option>
                        <option value="3">3 camadas</option>
                        <option value="4">4 camadas</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Massa crua por forma (g) *</label>
                      <input name="massa_crua_por_forma" type="number" {...noScrollNumber} value={form.massa_crua_por_forma}
                        onChange={handleChange} placeholder="420"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Discos por forma *</label>
                      <input name="discos_por_forma" type="number" {...noScrollNumber} min="1" value={form.discos_por_forma}
                        onChange={handleChange} placeholder="2"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300" />
                    </div>
                  </div>

                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-2">
                    {form.tipo === 'congelado' ? 'Embalagens' : 'Preço e Embalagens'}
                  </h3>
                  {form.tipo !== 'congelado' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Preço do bolo (R$)</label>
                        <input name="preco" type="text" value={form.preco}
                          onChange={e => {
                            const raw = e.target.value.replace(/\D/g, '');
                            if (!raw) { setForm(p => ({ ...p, preco: '' })); return; }
                            setForm(p => ({ ...p, preco: (parseInt(raw, 10) / 100).toFixed(2).replace('.', ',') }));
                          }}
                          placeholder="0,00"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300" />
                      </div>
                    </div>
                  )}
                  {form.tipo === 'congelado' && (
                    <p className="text-xs text-gray-400 -mt-1">O preço de cada sabor (massa+recheio) é definido no passo <strong>Menu Fixo</strong>.</p>
                  )}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-gray-500">Embalagens</p>
                      <button type="button" onClick={() => setEmbalagensSelecionadas([...embalagensSelecionadas, { embalagem_id: '', quantidade: 1 }])}
                        className="text-xs text-pink-600 hover:text-pink-800 font-medium">+ Adicionar</button>
                    </div>
                    {embalagensSelecionadas.length === 0 && (
                      <p className="text-xs text-gray-400">Nenhuma embalagem associada.</p>
                    )}
                    {embalagensSelecionadas.map((emb, idx) => (
                      <div key={idx} className="flex items-center gap-2 mb-2">
                        <select value={emb.embalagem_id}
                          onChange={e => {
                            const updated = [...embalagensSelecionadas];
                            updated[idx] = { ...updated[idx], embalagem_id: parseInt(e.target.value) || '' };
                            setEmbalagensSelecionadas(updated);
                          }}
                          className="flex-1 min-w-0 truncate border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
                          <option value="">Selecione...</option>
                          {embalagensList.filter(e => (e.categoria || '').toLowerCase() !== 'cakeboards').map(e => <option key={e.id} value={e.id}>{e.nome} ({e.categoria || 'sem cat.'})</option>)}
                        </select>
                        <input type="number" {...noScrollNumber} min="1" value={emb.quantidade}
                          onChange={e => {
                            const updated = [...embalagensSelecionadas];
                            updated[idx] = { ...updated[idx], quantidade: parseInt(e.target.value) || 1 };
                            setEmbalagensSelecionadas(updated);
                          }}
                          className="w-16 border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-center" />
                        <button type="button" onClick={() => setEmbalagensSelecionadas(embalagensSelecionadas.filter((_, i) => i !== idx))}
                          className="text-red-400 hover:text-red-600 text-sm font-bold">×</button>
                      </div>
                    ))}
                  </div>

                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-2">Componentes de montagem</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'Discos *', c: 'discos_camadas', g: 'discos_gramas', obrigatorio: true },
                      { label: 'Calda', c: 'calda_camadas', g: 'calda_gramas', obrigatorio: false },
                      { label: 'Cinta', c: 'cinta_camadas', g: 'cinta_gramas', obrigatorio: false },
                      { label: 'Cobertura', c: 'cobertura_camadas', g: 'cobertura_gramas', obrigatorio: false },
                    ].map(comp => {
                      const desabilitado = !comp.obrigatorio && form[comp.c] === '0' && form[comp.g] === '0';
                      return (
                        <div key={comp.label} className={`rounded-lg p-3 ${desabilitado ? 'bg-gray-100 opacity-60' : 'bg-gray-50'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-semibold text-gray-500">{comp.label}</p>
                            {!comp.obrigatorio && (
                              <label className="flex items-center gap-1.5 cursor-pointer select-none">
                                <input type="checkbox" checked={desabilitado}
                                  onChange={e => {
                                    if (e.target.checked) setForm(p => ({ ...p, [comp.c]: '0', [comp.g]: '0' }));
                                    else setForm(p => ({ ...p, [comp.c]: '', [comp.g]: '' }));
                                  }}
                                  className="accent-gray-400 w-3.5 h-3.5" />
                                <span className="text-xs text-gray-400">N/A</span>
                              </label>
                            )}
                          </div>
                          {!desabilitado && (
                            <>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Camadas</label>
                                  <input name={comp.c} type="number" {...noScrollNumber} value={form[comp.c]} onChange={handleChange}
                                    className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300" />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">g/camada</label>
                                  <input name={comp.g} type="number" {...noScrollNumber} value={form[comp.g]} onChange={handleChange}
                                    className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300" />
                                </div>
                              </div>
                              {form[comp.c] && form[comp.g] && (
                                <p className="text-xs text-pink-600 mt-1">Total: {Number(form[comp.c]) * Number(form[comp.g])}g</p>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Step 2: Massas ── */}
              {step === 2 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Sabores de massa permitidos</h3>
                  <p className="text-sm text-gray-500 mb-4">Quais massas podem ser usadas no tamanho <strong>{form.nome}</strong>?</p>
                  {fichasMassa.length === 0 && massasInativasVinculadas.length === 0 ? (
                    <p className="text-sm text-gray-400">Nenhuma ficha de massa cadastrada.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {fichasMassa.map(f => (
                        <button key={f.id} type="button"
                          onClick={() => setMassesSelecionadas(p =>
                            p.includes(Number(f.id)) ? p.filter(id => id !== Number(f.id)) : [...p, Number(f.id)]
                          )}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                            massesSelecionadas.includes(Number(f.id))
                              ? 'bg-pink-600 text-white border-pink-600'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-pink-300'
                          }`}>
                          {f.nome}
                        </button>
                      ))}
                      {massasInativasVinculadas.map(f => (
                        <button key={`inativa-${f.id}`} type="button"
                          onClick={() => setMassesSelecionadas(p =>
                            p.includes(Number(f.id)) ? p.filter(id => id !== Number(f.id)) : [...p, Number(f.id)]
                          )}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                            massesSelecionadas.includes(Number(f.id))
                              ? 'bg-gray-400 text-white border-gray-400'
                              : 'bg-gray-100 text-gray-400 border-gray-200'
                          }`}>
                          {f.nome} <span style={{ fontSize: 10, opacity: 0.7 }}>(inativa)</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── Step 3: Recheios ── */}
              {step === 3 && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Recheios disponíveis para {form.nome}</h3>
                    <p className="text-sm text-gray-500 mb-3">
                      Selecione os recheios do menu e defina as gramas por camada para este tamanho.
                      {nCamadas === 3 && ' As combinações serão configuradas no próximo passo.'}
                    </p>

                    {/* Recheios já cadastrados */}
                    {recheiosExistentes.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-400">
                            {recheiosSelecionados.filter(r => !r.isNew).length}/{recheiosExistentes.length} selecionados
                          </span>
                          <div className="flex gap-3">
                            <button type="button"
                              onClick={() => {
                                recheiosExistentes.forEach(r => {
                                  if (!recheiosSelecionados.find(x => x.id === r.id)) toggleRecheioExistente(r);
                                });
                              }}
                              className="text-xs text-pink-600 hover:text-pink-800 font-medium">
                              Selecionar todos
                            </button>
                            <button type="button"
                              onClick={() => setRecheiosSelecionados(p => p.filter(r => r.isNew))}
                              className="text-xs text-gray-400 hover:text-gray-600 font-medium">
                              Limpar
                            </button>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {recheiosExistentes.map(r => {
                            const sel = recheiosSelecionados.find(x => x.id === r.id);
                            const fichaInativa = (r.base_ficha_id && r.base_ficha_ativo === false) || (r.complemento_ficha_id && r.complemento_ficha_ativo === false);
                            return (
                              <button key={r.id} type="button" onClick={() => toggleRecheioExistente(r)}
                                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                                  sel ? (fichaInativa ? 'bg-gray-400 text-white border-gray-400' : 'bg-pink-600 text-white border-pink-600')
                                    : fichaInativa ? 'bg-gray-100 text-gray-400 border-gray-200' : 'bg-white text-gray-600 border-gray-200 hover:border-pink-300'
                                }`}>
                                {r.nome} {fichaInativa && <span style={{ fontSize: 10, opacity: 0.7 }}>(ficha inativa)</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Cards dos recheios selecionados */}
                  {recheiosSelecionados.length > 0 && (
                    <div className="space-y-3">
                      {recheiosSelecionados.map(r => {
                        const key = chaveRecheio(r);
                        const recheioBase = !r.isNew ? recheiosExistentes.find(x => x.id === r.id) : null;
                        const mostrarCompl = r.isNew ? r.tem_complemento : !!(recheioBase?.complemento_ficha_id || recheioBase?.complemento_produto_id);
                        return (
                          <div key={key} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                            <div className="flex items-center justify-between mb-3">
                              {r.isNew ? (
                                <input value={r.nome}
                                  onChange={e => atualizarRecheio(key, 'nome', e.target.value)}
                                  placeholder="Nome do recheio"
                                  className="font-medium text-gray-700 text-sm bg-transparent border-b border-gray-300 focus:outline-none focus:border-pink-400 flex-1 mr-2" />
                              ) : (
                                <span className="font-medium text-gray-700 text-sm">{r.nome}</span>
                              )}
                              <button type="button" onClick={() => removerRecheio(key)}
                                className="text-gray-300 hover:text-red-400 text-xl leading-none">×</button>
                            </div>

                            {/* Novo recheio: definir base */}
                            {r.isNew && (
                              <div className="space-y-3 mb-3 pb-3 border-b border-gray-200">
                                <div>
                                  <label className="text-xs text-gray-500 font-medium mb-1.5 block">Base *</label>
                                  <div className="flex gap-2 mb-2">
                                    {[['ficha', '📋 Ficha técnica'], ['produto', '📦 Produto']].map(([t, l]) => (
                                      <button key={t} type="button" onClick={() => atualizarRecheio(key, 'base_tipo', t)}
                                        className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                                          r.base_tipo === t ? 'bg-gray-700 text-white border-gray-700' : 'border-gray-200 text-gray-500 hover:border-gray-400'
                                        }`}>{l}</button>
                                    ))}
                                  </div>
                                  {r.base_tipo === 'ficha' ? (
                                    <select value={r.base_ficha_id} onChange={e => atualizarRecheio(key, 'base_ficha_id', e.target.value)}
                                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300">
                                      <option value="">Selecione a ficha...</option>
                                      {fichasRecheio.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                                    </select>
                                  ) : (
                                    <select value={r.base_produto_id} onChange={e => atualizarRecheio(key, 'base_produto_id', e.target.value)}
                                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300">
                                      <option value="">Selecione o produto...</option>
                                      {produtos.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
                                    </select>
                                  )}
                                </div>

                                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                                  <input type="checkbox" checked={r.tem_complemento}
                                    onChange={e => atualizarRecheio(key, 'tem_complemento', e.target.checked)}
                                    className="accent-pink-600 w-4 h-4" />
                                  Tem complemento?
                                </label>

                                {r.tem_complemento && (
                                  <div>
                                    <label className="text-xs text-gray-500 font-medium mb-1.5 block">Complemento</label>
                                    <div className="flex gap-2 mb-2">
                                      {[['ficha', '📋 Ficha técnica'], ['produto', '📦 Produto']].map(([t, l]) => (
                                        <button key={t} type="button" onClick={() => atualizarRecheio(key, 'complemento_tipo', t)}
                                          className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                                            r.complemento_tipo === t ? 'bg-gray-700 text-white border-gray-700' : 'border-gray-200 text-gray-500 hover:border-gray-400'
                                          }`}>{l}</button>
                                      ))}
                                    </div>
                                    {r.complemento_tipo === 'ficha' ? (
                                      <select value={r.complemento_ficha_id} onChange={e => atualizarRecheio(key, 'complemento_ficha_id', e.target.value)}
                                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300">
                                        <option value="">Selecione o complemento...</option>
                                        {fichasRecheio.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                                      </select>
                                    ) : (
                                      <select value={r.complemento_produto_id} onChange={e => atualizarRecheio(key, 'complemento_produto_id', e.target.value)}
                                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300">
                                        <option value="">Selecione o produto...</option>
                                        {produtos.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
                                      </select>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Gramas por camada */}
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Base — g/camada *</label>
                                <input type="number" {...noScrollNumber} value={r.gramas_base}
                                  onChange={e => atualizarRecheio(key, 'gramas_base', e.target.value)}
                                  placeholder="140"
                                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300" />
                              </div>
                              {mostrarCompl && (
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Complemento — g/camada</label>
                                  <input type="number" {...noScrollNumber} value={r.gramas_complemento}
                                    onChange={e => atualizarRecheio(key, 'gramas_complemento', e.target.value)}
                                    placeholder="60"
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300" />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <p style={{ fontSize: 12, color: '#9CA3AF', textAlign: 'center', marginTop: 8 }}>
                    Para cadastrar novos recheios, acesse <Link to="/guia/recheios" style={{ color: '#C4786E', fontWeight: 600 }}>Bolos → Recheios</Link>.
                  </p>
                </div>
              )}

              {/* ── Step 4: Sabores fixos (congelado) ── */}
              {step === 4 && isCongelado && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Menu Fixo</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Marque os <strong>sabores fixos</strong> (combinações massa + recheio) que você produz e mantém prontos no freezer.
                      Cada um fica disponível no menu com preço próprio. As embalagens são herdadas do tamanho.
                    </p>
                  </div>

                  {skusMenu.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-6">
                      Selecione massas e recheios nos passos anteriores para gerar as combinações.
                    </p>
                  ) : (
                    (() => {
                      const grupos = {};
                      for (const s of skusMenu) {
                        const k = String(s.massa_ficha_id);
                        if (!grupos[k]) grupos[k] = { nome: s.massa_nome, itens: [] };
                        grupos[k].itens.push(s);
                      }
                      return (
                        <div className="space-y-3">
                          {Object.entries(grupos).map(([massaId, g]) => (
                            <div key={massaId} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                              <div className="text-sm font-semibold text-gray-700 mb-3">{g.nome}</div>
                              <div className="space-y-2">
                                {g.itens.map(s => (
                                  <div key={s.key} className="flex items-center gap-3 bg-white rounded-lg px-3 py-2 border border-gray-100">
                                    <label className="flex items-center gap-2 flex-1 cursor-pointer select-none">
                                      <input type="checkbox" checked={s.ativo}
                                        onChange={() => toggleSku(s.key)}
                                        className="accent-pink-600 w-4 h-4" />
                                      <span className={`text-sm ${s.ativo ? 'text-gray-800 font-medium' : 'text-gray-500'}`}>
                                        {s.recheio_nome}
                                      </span>
                                    </label>
                                    {s.ativo && (
                                      <div className="flex items-center gap-1.5">
                                        <span className="text-xs text-gray-400">R$</span>
                                        <input type="text" value={s.preco}
                                          onChange={e => atualizarPrecoSku(s.key, e.target.value)}
                                          placeholder="0,00"
                                          className="w-24 border border-gray-200 rounded-lg px-2 py-1 text-sm text-right focus:outline-none focus:ring-2 focus:ring-pink-300" />
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()
                  )}
                </div>
              )}

              {/* ── Step 4: Combinações (imediato com 3 camadas) ── */}
              {step === 4 && !isCongelado && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Distribuição das {nCamadas} camadas</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Para cada par de recheios, defina como as {nCamadas} camadas se distribuem.
                    </p>
                  </div>
                  {combinacoes.map(c => (
                    <div key={c.key} className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                      <div className="text-center mb-4">
                        <div className="text-base font-semibold text-gray-800">
                          <span className="text-pink-700">{c.nomeA}</span>
                          <span className="text-gray-400 mx-2">+</span>
                          <span className="text-purple-700">{c.nomeB}</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-white rounded-lg p-3 border border-pink-100">
                          <div className="text-sm font-semibold text-pink-700 mb-2">Camada de {c.nomeA}</div>
                          <select value={c.camadas_a}
                            onChange={e => atualizarCombinacao(c.key, 'camadas_a', e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300">
                            {Array.from({ length: nCamadas - 1 }, (_, i) => i + 1).map(n => (
                              <option key={n} value={n}>{n} camada{n > 1 ? 's' : ''}</option>
                            ))}
                          </select>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-purple-100">
                          <div className="text-sm font-semibold text-purple-700 mb-2">Camada de {c.nomeB}</div>
                          <div className="border border-gray-100 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-500">
                            {c.camadas_b} camada{c.camadas_b !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Nome da combinação</label>
                        <input value={c.nome}
                          onChange={e => setCombinacoes(p => p.map(cc => cc.key === c.key ? { ...cc, nome: e.target.value } : cc))}
                          className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer — no celular sao tres acoes disputando 390px: sem
                whitespace-nowrap, "Salvar e fechar" quebrava uma palavra por
                linha e o rodape virava um bloco de quatro alturas. O texto
                encolhe para "Salvar" ali, e so o botao principal cresce. */}
            <div className="flex items-center justify-between gap-2 px-3 py-3 sm:px-6 sm:py-4 border-t border-gray-100"
              style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom))' }}>
              <button type="button"
                onClick={() => step > 1 ? setStep(s => s - 1) : fecharWizard()}
                className="px-2 sm:px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium whitespace-nowrap shrink-0"
                disabled={salvando}>
                {step > 1 ? '← Voltar' : 'Cancelar'}
              </button>

              {salvando ? (
                <div className="flex items-center gap-2 text-sm text-pink-600">
                  <span className="inline-block w-4 h-4 border-2 border-pink-400 border-t-transparent rounded-full animate-spin"></span>
                  <span>{salvarMsg}</span>
                </div>
              ) : isLastStep ? (
                <button type="button" onClick={salvar} disabled={salvando || !step3Valido()}
                  className="bg-pink-600 hover:bg-pink-700 disabled:opacity-60 text-white px-5 sm:px-6 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors">
                  ✓ Salvar tudo
                </button>
              ) : (
                <div className="flex items-center gap-2 min-w-0">
                  {editando && (
                    <button type="button" onClick={salvar}
                      disabled={salvando || !step1Valido()}
                      className="bg-white border border-pink-300 text-pink-600 hover:bg-pink-50 disabled:opacity-50 px-3 sm:px-5 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors">
                      ✓ <span className="hidden sm:inline">Salvar e fechar</span><span className="sm:hidden">Salvar</span>
                    </button>
                  )}
                  <button type="button" onClick={avancar}
                    disabled={(step === 1 && !step1Valido()) || (step === 2 && !step2Valido())}
                    className="bg-pink-600 hover:bg-pink-700 disabled:opacity-50 text-white px-4 sm:px-6 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors">
                    Próximo →
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  );
}


function TamanhoCard({ t, accent, preco, precoTitle, precoSubtitle, onEdit, onDelete }) {
  const accentBar = accent === 'blue' ? 'bg-blue-400' : 'bg-orange-400';
  // Compacto de proposito: o card antigo gastava tres linhas — titulo, uma
  // grade de 3 colunas com rotulo em cima do valor, e uma linha so para os
  // botoes, com divisoria. Cabiam dois cards e meio na tela do celular para
  // mostrar tres numeros cada. Agora sao duas linhas e os botoes dividem a
  // segunda com os atributos.
  const attrs = [
    ['Massa', t.massa_crua_por_forma ? `${t.massa_crua_por_forma}g` : null],
    ['Forma', t.forma_tamanho || null],
    ['Board', t.cakeboard_tamanho || null],
  ].filter(([, v]) => v);
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col">
      <div className={`h-1 ${accentBar}`} />
      <div className="p-3 flex flex-col gap-2 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-semibold text-gray-800 text-[15px] leading-tight truncate">{t.nome}<ExemploBadge origem={t.origem} /></div>
            <div className="text-[11px] text-gray-500 mt-0.5">{t.recheio_camadas} cam.</div>
          </div>
          <div className="text-right shrink-0" title={precoTitle}>
            <div className="text-green-700 font-bold text-sm whitespace-nowrap">{preco}</div>
            {precoSubtitle && <div className="text-[10px] text-gray-400">{precoSubtitle}</div>}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 min-w-0 text-xs text-gray-700">
            {attrs.map(([rotulo, valor]) => (
              <span key={rotulo} className="whitespace-nowrap">
                <span className="text-[10px] uppercase tracking-wider text-gray-400 mr-1">{rotulo}</span>
                <span className="font-semibold">{valor}</span>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button onClick={onEdit} title="Editar" aria-label="Editar"
              className="w-9 h-9 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center transition-colors">
              <FaEdit size={15} />
            </button>
            <button onClick={onDelete} title="Remover" aria-label="Remover"
              className="w-9 h-9 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 flex items-center justify-center transition-colors">
              <FaTrash size={15} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TamanhosTable({ rows, precoFor, recheioFor, onEdit, onDelete }) {
  return (
    <div className="overflow-x-auto bg-white rounded-xl border border-gray-200">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Nome</th>
            <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Fatias/Kg</th>
            <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Recheio</th>
            <th className="text-right px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Massa Crua</th>
            <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Forma</th>
            <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Cakeboard</th>
            <th className="text-right px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Preço</th>
            <th className="text-center px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Ações</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(t => (
            <tr key={t.id} className="border-t border-gray-100">
              <td className="px-3 py-2 font-semibold text-gray-800">{t.nome}</td>
              <td className="px-3 py-2 text-gray-600">{formatFatiasKg(t.fatias, t.tipo_medida)}</td>
              <td className="px-3 py-2 text-gray-600">{recheioFor(t)}</td>
              <td className="px-3 py-2 text-right text-gray-600">{t.massa_crua_por_forma ? `${t.massa_crua_por_forma}g` : '—'}</td>
              <td className="px-3 py-2 text-gray-600">{t.forma_tamanho || '—'}</td>
              <td className="px-3 py-2 text-gray-600">{t.cakeboard_tamanho || '—'}</td>
              <td className="px-3 py-2 text-right text-green-700 font-semibold whitespace-nowrap">{precoFor(t)}</td>
              <td className="px-3 py-2 text-center">
                <div className="inline-flex gap-1">
                  <button onClick={() => onEdit(t)} title="Editar"
                    className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 inline-flex items-center justify-center">
                    <FaEdit size={14} />
                  </button>
                  <button onClick={() => onDelete(t.id)} title="Remover"
                    className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 inline-flex items-center justify-center">
                    <FaTrash size={14} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Attr({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-0.5">{label}</div>
      <div className="text-gray-700 text-xs truncate" title={value}>{value}</div>
    </div>
  );
}